const $inject = ['travelService', 'dateTimeService', 'flightFilterHelperService', 'itinerariesService'];

const flightFilterPackageService = function flightFilterPackageService(
  travelService,
  dateTimeService,
  flightFilterHelperService,
  itinerariesService
) {
  const service = {
    process: process,
    sortProcess: sortProcess,
    updateOptions: updateOptions,

    countStops: countStops,
    countAirports: countAirports,
    countCarrier: countCarrier,
    countPolicy: countPolicy,
    countCarbonFootprint: countCarbonFootprint,
    countLayoverOptions: countLayoverOptions,
    countCabin: countCabin,
    countAircraft: countAircraft,
    countFlexibility: countFlexibility,
    countSAFScore: countSAFScore,
    countPrice: countPrice,
    countTime: countTime,
    filterSegmentOptions: filterSegmentOptions,
    filterFareOptions: filterFareOptions,
    filterLayover: filterLayover,
    filterPrice: filterPrice,
    filterFlightTime: filterFlightTime,
    filterCheapestFares: filterCheapestFares,
    filterPackageSegments: filterPackageSegments,
    filterCarbonFootprint: filterCarbonFootprint,
  };
  const vm = this;

  const NO_MATCH = 'NO_MATCH';

  function process(filters, activeFltr, opt, bumpFiltersApplied, getFiltersApplied, shouldCleanOptions) {
    if (shouldCleanOptions) {
      reselectOptions(filters);
    }

    if (!filters.carrier.options.length) {
      updateOptions(filters, null, null, shouldCleanOptions);
    }

    resetVisibility();
    let packages = travelService.packageResults;
    Object.values(filters)
      .filter((flt) => flt.pkgFilter)
      .forEach((flt) => flt.pkgFilter(packages, bumpFiltersApplied));
    packages.forEach((pkg) => {
      let allSegmentsVisible = pkg.Segments.every((segment) => segment.visible);
      pkg.visible = allSegmentsVisible;
      pkg.filteredBySegments = !allSegmentsVisible;
      if (!allSegmentsVisible) {
        pkg.filteredBy = [
          ...new Set([...pkg.filteredBy, ...pkg.Segments.filter((d) => !d.visible && d.filteredBy).flatMap((d) => d.filteredBy)]),
        ];
      }
    });

    packages.forEach((pkg) => {
      const fareClasses = Object.values(pkg.FareClasses).filter((fc) => fc);
      pkg.ActiveMinRate = 0;
      fareClasses.forEach((fc) => {
        fc.visible = fc.Fares.some((fare) => fare.visible);
        if (fc.visible) {
          fc.Fares.filter((fare) => fare.visible && (pkg.ActiveMinRate === 0 || fare.Amount < pkg.ActiveMinRate)).forEach((fare) => {
            if (pkg.ActiveMinRate > fare.Amount || pkg.ActiveMinRate == 0) pkg.ActiveMinRate = fare.Amount;
          });
        }
      });
      let hasVisibleFares = fareClasses.some((fc) => fc.visible);
      pkg.filteredByFares = !hasVisibleFares;

      pkg.visible = pkg.visible ? hasVisibleFares : pkg.visible;
    });

    updateOptions(filters, activeFltr, opt, shouldCleanOptions);
  }

  function sortProcess(data) {
    if (!data.property) return;
    let packages = travelService.packageResults;
    const getPropertySum = (pkg, property) => {
      switch (property) {
        case 'Amount':
          const cleanedFareClasses = Object.values(pkg.FareClasses).filter((fc) => fc !== null);
          return Math.min(...cleanedFareClasses.map((fc) => fc.Details.Amount));
        case 'Recommended':
          return pkg[property];
        case 'DepartureDateTime':
        case 'ArrivalDateTime':
          return new Date(pkg.Segments[0].flight[property]);
        case 'Preferred':
          return pkg.Segments[0].flight.Preferred ? 0 : 1;
        case 'MinRate':
          return pkg.ActiveMinRate;
        default:
          return pkg.Segments.reduce((sum, sgmt) => sum + sgmt.flight[property], 0);
      }
    };

    packages.sort((a, b) => {
      const aSum = getPropertySum(a, data.property);
      const bSum = getPropertySum(b, data.property);
      return data.isReverse ? bSum - aSum : aSum - bSum;
    });
  }

  function updateOptions(filters, activeFltr, opt, shouldCleanOptions) {
    let packages = travelService.packageResults;

    createStopOptions(filters);
    createTimeOptions(filters);
    createPriceOptions(filters);
    createCarbonOptions(filters);
    createCabinOptions(filters);
    createPolicyOptions(filters);
    createFlexibility(filters);
    createLayoverOptions(filters, activeFltr);

    for (let i = 0; i < packages.length; i++) {
      const pkg = packages[i];
      const segments = pkg.Segments;

      for (let j = 0; j < segments.length; j++) {
        const segment = segments[j].flight;
        createCarrierOptions(filters, segment, j);
        createAirportsOptions(filters, segment, j);
        createAircraftOptions(filters, segment, j);
        createSafScoreOptions(filters, segment, j);
      }
    }

    if (shouldCleanOptions) {
      cleanOptions(filters);
    }

    const segmentsQuantity = packages[0].Segments.length;

    addAllPreferredCarriers(filters);

    filters = Object.values(filters);

    filters.forEach((flt) => {
      if (filterHasIncludeExcludeSelectionsUnset(flt)) {
        if (segmentsQuantity == 0) {
          flt.includeExcludeSelections = [];
        } else {
          flt.includeExcludeSelections = new Array(segmentsQuantity + 1).fill(true);
        }
      }
      if (flt.options) flt.options.forEach((opt) => (Array.isArray(opt) ? opt.forEach((o) => (o.count = 0)) : (opt.count = 0)));
    });

    filters.filter((f) => f.pkgCount).forEach((f) => f.pkgCount(packages));
  }

  function resetVisibility() {
    travelService.packageResults.forEach((pkg) => {
      pkg.visible = true;
      pkg.filteredByFares = false;
      pkg.filteredBySegments = false;
      pkg.filteredBy = [];
      Object.values(pkg.FareClasses)
        .filter((fare) => fare)
        .forEach((fc) => {
          fc.visible = true;
          fc.Fares.forEach((fare) => {
            fare.visible = true;
            fare.filteredBy = [];
          });
        });
      pkg.Segments.forEach((segment) => {
        segment.visible = true;
        segment.filteredBy = [];
      });
    });
  }

  function createCarrierOptions(filters, segment, i) {
    const options = filters.carrier.options;
    if (!options[i]) options[i] = [];

    // ALL tab index is set to be the length of the results.
    var allTabIndex = travelService.results.length;
    if (!options[allTabIndex]) options[allTabIndex] = [];

    const key = segment.Carrier;
    const id = 'carrier-' + key;
    const option = options[i].find((opt) => opt.id === id);

    if (!option) {
      options[i].push({
        label: segment.CarrierName,
        value: key,
        count: 0,
        id: id,
        preferred: segment.Preferred,
      });
    }

    // ALL tab
    const allTabOption = options[allTabIndex].find((opt) => opt.id === id);
    if (!allTabOption) {
      options[allTabIndex].push({
        label: segment.CarrierName,
        value: key,
        count: 0,
        id: id,
        preferred: segment.Preferred,
      });
    }
  }

  function createAircraftOptions(filters, segment, i) {
    const options = filters.aircraft.options;
    if (!options[i]) options[i] = [];

    // ALL tab index is set to be the length of the results.
    var allTabIndex = travelService.results.length;
    if (!options[allTabIndex]) options[allTabIndex] = [];

    for (let leg of segment.Details) {
      if (!leg.EquipmentType) continue;
      const key = leg.EquipmentType;
      const id = 'aircraft-' + key.replace(/[^a-zA-Z0-9]/g, '');

      const option = options[i].find((opt) => opt.id === id);
      if (!option) {
        options[i].push({
          label: leg.EquipmentType,
          value: key,
          count: 0,
          id: id,
        });
        options[i].sort((a, b) => a.label.localeCompare(b.label));
      }

      // ALL tab
      const allTabOption = options[allTabIndex].find((opt) => opt.id === id);
      if (!allTabOption) {
        options[allTabIndex].push({
          label: leg.EquipmentType,
          value: key,
          count: 0,
          id: id,
        });
        options[allTabIndex].sort((a, b) => a.label.localeCompare(b.label));
      }
    }
  }

  function createSafScoreOptions(filters, sr, i) {
    const options = filters.safScore.options;
    if (!options[i]) options[i] = [];

    // ALL tab index is set to be the length of the results.
    var allTabIndex = travelService.results.length;
    if (!options[allTabIndex]) options[allTabIndex] = [];
    if (sr.SafScore) {
      const key = sr.SafScore;
      const id = 'safScore-' + key;
      const option = options[i].find((opt) => opt.id === id);
      if (!option) {
        options[i].push({
          label: sr.SafScore,
          value: key,
          count: 1,
          id: id,
        });
      }

      // ALL tab
      const allTabOption = options[allTabIndex].find((opt) => opt.id === id);
      if (!allTabOption) {
        options[allTabIndex].push({
          label: sr.SafScore,
          value: key,
          count: 1,
          id: id,
        });
      }
    }
  }

  function createAirportsOptions(filters, segment, i) {
    const options = filters.airports.options;
    if (!options[i]) options[i] = [];
    segment.Legs.forEach((leg) => {
      const key = leg.Origin.Code;
      const id = 'airport-' + key;
      const option = options[i].find((opt) => opt.id === id);
      if (option) return;
      options[i].push({
        label: leg.Origin.City + ' - ' + key,
        value: key,
        count: 1,
        id: id,
      });
    });
    options[i].sort((a, b) => a.label.localeCompare(b.label));
  }

  function createLayoverOptions(filters, activeFltr) {
    if (activeFltr && activeFltr.id === 'layover') return;
    const filter = filters.layover;
    const options = filter.options;
    if (options.length) return;

    travelService.packageResults[0].Segments.forEach((segment, i) => {
      const layoverInMinutes = travelService.packageResults.map((pkg) => pkg.Segments[i].flight.TotalLayoverInMinutes);
      const min = Math.min(...layoverInMinutes);
      const max = Math.max(...layoverInMinutes);
      options[i] = {
        min: 0,
        max: max,
        defaultMin: min,
        defaultMax: max,
      };
    });
  }

  function addAllPreferredCarriers(filters) {
    let options = filters.carrier.options;
    options.forEach((option, i) => {
      if (option.some((carrier) => carrier.value === 'all')) return;
      option.sort((a, b) => a.label.localeCompare(b.label));
      if (option.some((carrier) => carrier.preferred)) {
        option.unshift({
          label: 'All Preferred Airlines',
          value: 'all',
          id: `carrier-all-preferred-${i}`,
          preferred: true,
          count: 0,
        });
      }
    });
  }

  function createStopOptions(filters) {
    const options = filters.stops.options;

    // ALL tab index is set to be the length of the segments.
    for (var i = 0; i < travelService.packageResults[0].Segments.length + 1; i++) {
      if (options[i]) return;
      options[i] = [
        {
          label: 'Nonstop Only',
          value: 0,
          id: `stops-${i}-0`,
        },
        {
          label: '2 stops',
          value: 2,
          id: `stops-${i}-2`,
        },
        {
          label: '1 stop',
          value: 1,
          id: `stops-${i}-1`,
        },
        {
          label: '3 or more stops',
          value: 'all',
          id: `stops-${i}-all`,
        },
      ];
    }
  }

  function createPolicyOptions(filters) {
    const options = filters.policy.options;
    const flyAmericaActEnabled = itinerariesService.setFlyAmericaEnabled();

    var newOptions = [
      {
        label: 'In policy',
        value: 'in',
        id: 'policy-in',
      },
      {
        label: 'Out of policy',
        value: 'out',
        id: 'policy-out',
      },
    ];

    if (flyAmericaActEnabled) {
      newOptions.push({
        label: 'Fly America Policy',
        value: 'flyAmerica',
        id: 'policy-flyAmerica',
      });
    }

    // ALL tab index is set to be the length of the results.
    for (var i = 0; i < travelService.packageResults[0].Segments.length + 1; i++) {
      if (options[i]) return;
      options[i] = newOptions;
    }
  }

  function createPriceOptions(filters) {
    const filter = filters.priceRange;
    const options = filter.options;
    let min = Number.MAX_VALUE;
    let max = 0;

    travelService.packageResults.forEach((pkg) => {
      Object.values(pkg.FareClasses).forEach((fare) => {
        if (!fare) return;
        const fareAmount = Math.round(fare.Details.Amount);
        if (fareAmount < min) min = fareAmount;
        if (fareAmount > max) max = fareAmount;
      });
    });

    filter.rzslider.floor = min;
    filter.rzslider.ceil = max;

    var option = {
      min: min,
      max: max,
      defaultMin: min,
      defaultMax: max,
    };

    for (var i = 0; i < travelService.packageResults[0].Segments.length + 1; i++) {
      if (!options[i]) {
        options[i] = option;
      }
      if (!filter.rzslider[i]) {
        filter.rzslider[i] = {
          id: filter.id,
          floor: option.defaultMin,
          ceil: option.defaultMax,
          noSwitching: true,
          interval: 1000,
          translate: filter.rzsliderCallbacks.translate,
          onChange: filter.rzsliderCallbacks.onChange,
        };
      }
    }
  }

  function createCabinOptions(filters) {
    const options = filters.cabin.options;

    // ALL tab index is set to be the length of the results.
    var allTabIndex = travelService.results.length;
    if (!options[allTabIndex]) options[allTabIndex] = [];

    travelService.packageResults[0].Segments.forEach((segment, i) => {
      if (!options[i]) options[i] = [];

      travelService.pkgFareClasses.forEach((fareClass) => {
        const key = fareClass.replace(/\s/g, '');
        const id = 'cabin-' + key;

        var newOption = {
          label: fareClass,
          value: key,
          count: 0,
          id: id,
        };

        const option = options[i].find((opt) => opt.id === id);
        if (!option) {
          options[i].push(newOption);
        }

        const allTabOption = options[allTabIndex].find((opt) => opt.id === id);
        if (!allTabOption) {
          options[allTabIndex].push(newOption);
        }
      });

      options[i].sort((a, b) => a.label.localeCompare(b.label));
    });

    options[allTabIndex].sort((a, b) => a.label.localeCompare(b.label));
  }

  function createCarbonOptions(filters) {
    const filter = filters.carbonFootprintRange;
    const options = filter.options;
    let min = Number.MAX_VALUE;
    let max = 0;

    travelService.packageResults.forEach((pkg) => {
      Object.values(pkg.FareClasses).forEach((fare) => {
        if (!fare) return;
        const fareCarbonEmission = Math.round(fare.Details.CarbonEmission);
        if (fareCarbonEmission < min) min = fareCarbonEmission;
        if (fareCarbonEmission > max) max = fareCarbonEmission;
      });
    });

    filter.rzslider.floor = min;
    filter.rzslider.ceil = max;

    var option = {
      min: min,
      max: max,
      defaultMin: min,
      defaultMax: max,
    };

    for (var i = 0; i < travelService.packageResults[0].Segments.length + 1; i++) {
      if (!options[i]) {
        options[i] = option;
      }
    }
  }

  function createTimeOptions(filters) {
    const options = filters.flightTime.options;
    travelService.packageResults[0].Segments.forEach((segment, i) => {
      if (options[i]) return;
      options[i] = {
        defaultMinDepart: 0,
        defaultMaxDepart: 0,
        minDepart: 0,
        maxDepart: 1439,
        minArrive: 0,
        maxArrive: 1439,
        defaultMinArrive: 0,
        defaultMaxArrive: 0,
      };
    });
  }

  function createFlexibility(filters) {
    const options = filters.flexibility.options;

    var newOptions = [
      {
        label: 'Refundable',
        value: 'refundable',
        id: 'flexibility-refundable',
      },
      {
        label: 'Non-Refundable',
        value: 'nonRefundable',
        id: 'flexibility-nonRefundable',
      },
    ];

    // ALL tab index is set to be the length of the results.
    for (var i = 0; i < travelService.packageResults[0].Segments.length + 1; i++) {
      if (options[i]) return;
      options[i] = newOptions;
    }
  }

  function createInitialCounts(filter) {
    const counts = [];
    filter.options.forEach((opts) => {
      var option = {};
      opts.forEach((opt) => {
        option[opt.value] = 0;
      });
      counts.push(option);
    });

    return counts;
  }

  function getFilteredPackages(filter, packages) {
    var cleanPackages = {};

    travelService.packageResults[0].Segments.forEach((segment, i) => {
      var filteredPackages = packages.filter((pkg) => travelService.isVisibleOrOnlyFilteredBySegment(pkg, pkg.Segments[i], filter.id, i));
      filteredPackages.forEach((pkg) => {
        let segmentInfo = { index: i, segment: pkg.Segments[i] };
        if (cleanPackages[pkg.id]) {
          cleanPackages[pkg.id].push(segmentInfo);
        } else {
          cleanPackages[pkg.id] = [segmentInfo];
        }
      });
    });

    return cleanPackages;
  }

  function setCounts(filter, counts) {
    var allTabIndex = counts.length - 1;

    travelService.packageResults[0].Segments.forEach((segment, i) => {
      filter.options[i].forEach((opt) => {
        opt.count = counts[i][opt.value];
      });
    });

    // ALL tab
    filter.options[allTabIndex].forEach((opt) => {
      opt.count = counts[allTabIndex][opt.value];
    });
  }

  function countSpecifiedPropertyForAllAndSegmentTabs(packages, filter, propertyName) {
    const counts = createInitialCounts(filter);
    var cleanPackages = getFilteredPackages(filter, packages);
    var allTabIndex = counts.length - 1;

    for (const [packageId, segments] of Object.entries(cleanPackages)) {
      var currentValue = null;

      segments.forEach((segmentInfo, i) => {
        const property = segmentInfo.segment.flight[propertyName];
        counts[segmentInfo.index][property]++;

        if (currentValue != null) {
          if (currentValue != segmentInfo.segment.flight[propertyName]) {
            // If not all of them match, we don't care about it. We mark it with -1 so we can ignore it.
            currentValue = NO_MATCH;
          }
        } else {
          currentValue = segmentInfo.segment.flight[propertyName];
        }
      });
    }

    countSpecifiedPropertyForAllAndSegmentAllTabs(packages, filter, propertyName, counts);
    setCounts(filter, counts);
  }
  function countSpecifiedPropertyForAllAndSegmentAllTabs(packages, filter, propertyName, counts) {
    var allTabIndex = counts.length - 1;
    var filteredPackages = packages.filter(
      (pkg) => !pkg.filteredByFares && (pkg.visible || pkg.filteredBy.every((f) => f.startsWith(`${filter.id}_`)))
    );
    filteredPackages.forEach((pkg) => {
      let currentValue = pkg.Segments[0].flight[propertyName];
      if (pkg.Segments.every((segment) => segment.flight[propertyName] === currentValue)) {
        counts[allTabIndex][currentValue]++;
      }
    });
  }

  function countStops(packages) {
    countSpecifiedPropertyForAllAndSegmentTabs(packages, this, 'Stops');
  }

  function countAirports(packages) {
    travelService.packageResults[0].Segments.forEach((segment, i) => {
      packages.forEach((pkg) => {
        if (!travelService.isVisibleOrOnlyFilteredBySegment(pkg, pkg.Segments[i], this.id, i)) return;

        pkg.Segments[i].flight.Legs.forEach((leg) => {
          this.options[i].forEach((opt) => {
            if (leg.Origin.Code === opt.value) opt.count++;
          });
        });
      });
    });
  }

  function countCarrier(packages) {
    countSpecifiedPropertyForAllAndSegmentTabs(packages, this, 'Carrier');

    // Setting all preferred count
    this.options.forEach((optionSet) => {
      const sumPreferredCounts = optionSet.reduce((sum, option) => (option.preferred ? sum + option.count : sum), 0);
      const allOption = optionSet.find((option) => option.value === 'all');
      if (allOption) allOption.count = sumPreferredCounts;
    });
  }
  function countPrice(packages) {
    this.segmentCounts = 0;
    this.segmentVisibleCounts = 0;
    let defaultOptions = { min: 0, max: 0, defaultMin: 0, defaultMax: 0 };
    let previousOptions = this.options[0];
    let allAmounts = [[]];
    for (const pkg of packages) {
      const fareClasses = Object.values(pkg.FareClasses).filter((fc) => fc);
      fareClasses.forEach((fc) => {
        const visibleFares = fc.Fares.filter((fare) => travelService.isVisibleOrOnlyFilteredBy(pkg, fare, this.id));
        this.segmentCounts += visibleFares.length;
        this.segmentVisibleCounts += visibleFares.filter((fare) => fare.visible).length;
        allAmounts[0] = [...allAmounts[0], ...visibleFares.map((m) => m.Amount)];
      });
    }
    flightFilterHelperService.updatePreviousOptions(
      this.options[0],
      flightFilterHelperService.setPriceOptionForResult(allAmounts[0], previousOptions)
    );
    this.options[-1] = this.options[0];
    flightFilterHelperService.setSliderWithUpdatedOptions(this);
  }

  function countPolicy(packages) {
    const counts = {
      in: 0,
      out: 0,
      flyAmerica: 0,
    };
    for (const pkg of packages) {
      const fareClasses = Object.values(pkg.FareClasses).filter((fc) => fc);
      let [hasInPolicy, hasOutPolicy, hasFlyAmerica] = [false, false, false];
      fareClasses.forEach((fc) => {
        const visibleFares = fc.Fares.filter((fare) => travelService.isVisibleOrOnlyFilteredBy(pkg, fare, this.id));
        if (visibleFares.some((fare) => fare.PolicyExceptionDetails.InPolicy)) hasInPolicy = true;
        if (visibleFares.some((fare) => !fare.PolicyExceptionDetails.InPolicy)) hasOutPolicy = true;
        if (visibleFares.some((fare) => fare.PolicyExceptionDetails.FlyAmericaActInPolicy)) hasFlyAmerica = true;
      });
      if (hasInPolicy) counts.in++;
      if (hasOutPolicy) counts.out++;
      if (hasFlyAmerica) counts.flyAmerica++;
    }

    this.options.forEach((opt) =>
      opt.forEach((o) => {
        o.count = counts[o.value];
      })
    );
  }

  function countCarbonFootprint(packages) {
    this.segmentCounts = 0;
    this.segmentVisibleCounts = 0;
    const isOptionsDefined = this.options && this.options.length > 0;
    let defaultOptions = () => {
      return { min: 0, max: 0, defaultMin: 0, defaultMax: 0 };
    };
    let previousOptions = isOptionsDefined ? this.options[0] : defaultOptions();
    let allCarbonEmissions = [[]];
    for (const pkg of packages) {
      const fareClasses = Object.values(pkg.FareClasses).filter((fc) => fc);
      fareClasses.forEach((fc) => {
        const visibleFares = fc.Fares.filter((fare) => travelService.isVisibleOrOnlyFilteredBy(pkg, fare, this.id));
        this.segmentCounts += visibleFares.length;
        this.segmentVisibleCounts += visibleFares.filter((fare) => fare.visible).length;
        allCarbonEmissions[0] = [...allCarbonEmissions[0], ...visibleFares.map((m) => m.CarbonEmission)];
      });
    }
    var updatedOptions = flightFilterHelperService.setPriceOptionForResult(allCarbonEmissions[0], previousOptions);
    flightFilterHelperService.updatePreviousOptions(this.options[0], updatedOptions);
    flightFilterHelperService.setSliderWithUpdatedOptions(this);
  }

  function countLayoverOptions(packages) {
    this.segmentCounts = [];
    this.segmentVisibleCounts = [];
    const isOptionsDefined = this.options && this.options.length > 0;
    let defaultOptions = { min: 0, max: 0, defaultMin: 0, defaultMax: 0 };
    let allLayovers = [[]];

    packages[0].Segments.forEach((segment, i) => {
      let previousOptions = isOptionsDefined ? this.options[i] : defaultOptions;
      const visiblePackages = packages.filter((pkg) => travelService.isVisibleOrOnlyFilteredBySegment(pkg, pkg.Segments[i], this.id, i));
      allLayovers[i] = visiblePackages.map((m) => m.Segments[i].flight.TotalLayoverInMinutes);
      this.options[i] = flightFilterHelperService.setPriceOptionForResult(allLayovers[i], previousOptions);
      this.segmentCounts[i] = allLayovers[i].length;
      this.segmentVisibleCounts[i] = visiblePackages.filter((pkg) => pkg.visible).length;
    });

    flightFilterHelperService.setSliderWithUpdatedOptions(this);
  }

  function countTime(packages) {
    let defaultOptions = () => {
      return {
        defaultMinDepart: 0,
        defaultMaxDepart: 0,
        maxDepart: 0,
        minDepart: 0,
        maxArrive: 0,
        minArrive: 0,
        defaultMinArrive: 0,
        defaultMaxArrive: 0,
      };
    };
    let allDepartureTimes = [];
    let allArrivalTimes = [];

    packages[0].Segments.forEach((segment, i) => {
      const isOptionsDefined = this.options && this.options.length > 0;
      const previousOptions = isOptionsDefined ? this.options[i] : defaultOptions();

      allDepartureTimes[i] = packages
        .filter((pkg) => travelService.isVisibleOrOnlyFilteredBySegment(pkg, pkg.Segments[i], this.id, i))
        .map((pkg) => dateTimeService.getMinutesFromTime(pkg.Segments[i].flight.DepartureTime));

      allArrivalTimes[i] = packages
        .filter((pkg) => travelService.isVisibleOrOnlyFilteredBySegment(pkg, pkg.Segments[i], this.id, i))
        .map((pkg) => dateTimeService.getMinutesFromTime(pkg.Segments[i].flight.ArrivalTime));

      this.segmentCounts[i] = [];
      this.segmentCounts[i][0] = flightFilterHelperService.getTimeCounts(allDepartureTimes[i]);
      this.segmentCounts[i][1] = flightFilterHelperService.getTimeCounts(allArrivalTimes[i]);
      this.options[i] = flightFilterHelperService.setPriceOptionForResultTime(allDepartureTimes[i], allArrivalTimes[i], previousOptions);
    });

    flightFilterHelperService.setSliderWithUpdatedOptionsTime(this);
  }

  function countCabin(packages) {
    this.options.forEach((opt, index) =>
      opt.forEach(
        (o) =>
          (o.count = packages.filter(
            (pkg) => pkg.FareClasses[o.label] && travelService.isVisibleOrOnlyFilteredBy(pkg, pkg.FareClasses[o.label].DisplayFare, this.id)
          ).length)
      )
    );
  }

  function countAircraft(packages) {
    const counts = createInitialCounts(this);
    var cleanPackages = getFilteredPackages(this, packages);
    var allTabIndex = counts.length - 1;

    for (const [packageId, segments] of Object.entries(cleanPackages)) {
      if (segments.length == travelService.packageResults[0].Segments.length) {
        var packageEquipmentTypes = {};

        segments.forEach((segmentInfo, i) => {
          var segmentEquipmentTypes = {};
          segmentInfo.segment.flight.Details.forEach((leg) => {
            if (!segmentEquipmentTypes[leg.EquipmentType]) {
              segmentEquipmentTypes[leg.EquipmentType] = 1;
              counts[segmentInfo.index][leg.EquipmentType]++;
            }
          });

          for (const [equipmentType, count] of Object.entries(segmentEquipmentTypes)) {
            if (packageEquipmentTypes[equipmentType]) {
              packageEquipmentTypes[equipmentType]++;
            } else {
              packageEquipmentTypes[equipmentType] = 1;
            }
          }
        });

        // Only if the equipment type count is the same as the segment count, we increase the count for that particular equipment type.
        for (const [equipmentType, count] of Object.entries(packageEquipmentTypes)) {
          if (packageEquipmentTypes[equipmentType] == travelService.packageResults[0].Segments.length) {
            counts[allTabIndex][equipmentType]++;
          }
        }
      }
    }

    setCounts(this, counts);
  }

  function countFlexibility(packages) {
    let options = this.options;

    for (var i = 0; i < travelService.packageResults[0].Segments.length + 1; i++) {
      const counts = {
        refundable: 0,
        nonRefundable: 0,
      };
      packages.forEach((pkg) => {
        const FareClasses = Object.values(pkg.FareClasses).filter((fc) => fc);
        let [refundable, nonRefundable] = [false, false];
        FareClasses.forEach((fc) => {
          const visibleFares = fc.Fares.filter((f) => travelService.isVisibleOrOnlyFilteredBy(pkg, f, this.id));
          if (visibleFares.some((fare) => fare && fare.Refundable)) refundable = true;
          if (visibleFares.some((fare) => fare && !fare.Refundable)) nonRefundable = true;
        });
        if (refundable) counts.refundable++;
        if (nonRefundable) counts.nonRefundable++;

        options[i].forEach((o) => (o.count = counts[o.value]));
      });
    }
  }

  function countSAFScore(packages) {
    countSpecifiedPropertyForAllAndSegmentTabs(packages, this, 'SafScore');

    // Setting all preferred count
    this.options.forEach((optionSet) => {
      const sumPreferredCounts = optionSet.reduce((sum, option) => (option.preferred ? sum + option.count : sum), 0);
      const allOption = optionSet.find((option) => option.value === 'all');
      if (allOption) allOption.count = sumPreferredCounts;
    });
  }

  function filterSegmentOptions(packages, bumpFiltersApplied) {
    const includeExcludeSelections = this.includeExcludeSelections;
    packages[0].Segments.forEach((segment, i) => {
      const isAnySeleted = !!includeExcludeSelections && (includeExcludeSelections[i] === true || includeExcludeSelections[i] === false);
      const isSeleted = !!isAnySeleted ? includeExcludeSelections[i] : true;

      this.selected = this.selected || [];
      let values = this.selected[i] || [];
      if (!values.length) return;

      bumpFiltersApplied();
      packages.forEach((pkg) => {
        const segment = pkg.Segments[i];
        const flight = segment.flight;
        let segmentVisible = true;
        switch (this.id) {
          case 'airport':
            segmentVisible = flight.Legs.some((leg) => {
              const includesOriginCode = values.includes(leg.Origin.Code);
              return isSeleted ? includesOriginCode : !includesOriginCode;
            });
            break;
          case 'aircraft':
            segmentVisible = flight.Details.some((leg) => {
              const includesEquipmentType = values.includes(leg.EquipmentType);
              return isSeleted ? includesEquipmentType : !includesEquipmentType;
            });
            break;
          case 'carrier':
            values = values.filter((value) => value !== 'all');
            if (!values.length) return;
            segmentVisible = values.includes(flight.Carrier);
            segmentVisible = isSeleted ? segmentVisible : !segmentVisible;
            break;
          case 'safScore':
            values = values.filter((value) => value !== 'all');
            if (!values.length) return;
            segmentVisible = values.includes(flight.SafScore);
            segmentVisible = isSeleted ? segmentVisible : !segmentVisible;
            break;
          default:
            segmentVisible = values.includes(flight[this.reference]);
            segmentVisible = isSeleted ? segmentVisible : !segmentVisible;
            break;
        }
        segment.visible = segment.visible ? segmentVisible : segment.visible;
        if (!segmentVisible) {
          segment.filteredBy.push(travelService.GetIdSeg(this.id, i));
        }
      });
    });
  }

  function filterFareOptions(packages, bumpFiltersApplied) {
    this.selected = this.selected || [];
    const values = this.selected[0] || [];
    if (!values.length) return;

    bumpFiltersApplied();

    packages.forEach((pkg) => {
      const fareClasses = Object.entries(pkg.FareClasses).filter(([key, fc]) => fc);

      fareClasses.forEach(([key, fc]) => {
        let visibleFare = null;
        fc.Fares.forEach((fare) => {
          let isFareVisible = true;
          switch (this.id) {
            case 'cabin':
              isFareVisible = values.includes(key.replace(/\s/g, ''));
              break;
            case 'policy':
              isFareVisible = values.includes(fare.PolicyExceptionDetails.InPolicy ? 'in' : 'out');
              if (values.includes('flyAmerica')) isFareVisible = fare.PolicyExceptionDetails.FlyAmericaActInPolicy;
              break;

            case 'flexibility':
              isFareVisible = values.includes(fare.Refundable ? 'refundable' : 'nonRefundable');
              break;
          }
          fare.visible = fare.visible ? isFareVisible : fare.visible;
          if (!isFareVisible) {
            fare.filteredBy.push(this.id);
          }

          if (fare.visible && (!visibleFare || fare.Amount < visibleFare.Amount)) {
            fc.DisplayFare = fare;
            visibleFare = fare;
          }
        });

        fc.isVisible = fc.isVisible ? fc.Fares.some((fare) => fare.visible) : fc.isVisible;
      });
    });
  }

  function filterCarbonFootprint(packages, bumpFiltersApplied) {
    const option = this.options[0];
    const { min, max, defaultMin, defaultMax } = option;
    const isActive = min > defaultMin || max < defaultMax;
    if (!isActive) return;
    if (isActive && this.isResultsFilter) bumpFiltersApplied(this);
    packages.forEach((pkg) => {
      Object.values(pkg.FareClasses)
        .filter((fc) => fc)
        .forEach((fc) => {
          let cheapestFare = null;
          fc.Fares.filter((fare) => fare.visible).forEach((fare) => {
            let isFareVisible = true;
            const fareInRange = fare.CarbonEmission >= min && fare.CarbonEmission <= max;
            isFareVisible = fareInRange;
            fare.visible = fare.visible ? isFareVisible : fare.visible;
            if (!isFareVisible) {
              fare.filteredBy.push(this.id);
            }
            if (fareInRange) {
              if (!cheapestFare || fare.Amount < cheapestFare.Amount) cheapestFare = fare;
            }
          });
          if (cheapestFare) fc.DisplayFare = cheapestFare;
          fc.visible = !!cheapestFare;
        });
    });
  }

  function filterLayover(packages, bumpFiltersApplied) {
    packages[0].Segments.forEach((segment, i) => {
      const option = this.options[i];
      const { min, max, defaultMin, defaultMax } = option;
      const isActive = min > defaultMin || max < defaultMax;
      if (!isActive) return;
      bumpFiltersApplied(this, i);
      packages.forEach((pkg) => {
        let segmentVisible = false;
        const segment = pkg.Segments[i];

        const layover = segment.flight.TotalLayoverInMinutes;
        segmentVisible = layover >= min && layover <= max;
        segment.visible = segment.visible ? segmentVisible : segment.visible;
        if (!segmentVisible) {
          segment.filteredBy.push(travelService.GetIdSeg(this.id, i));
        }
      });
    });
  }

  function filterPrice(packages, bumpFiltersApplied) {
    const option = this.options[0];
    const { min, max, defaultMin, defaultMax } = option;
    const isActive = min > defaultMin || max < defaultMax;
    if (!isActive) return;
    if (isActive && this.isResultsFilter) bumpFiltersApplied(this);
    packages.forEach((pkg) => {
      Object.values(pkg.FareClasses)
        .filter((fc) => fc)
        .forEach((fc) => {
          let cheapestFare = null;
          fc.Fares.forEach((fare) => {
            let isFareVisible = true;
            const fareInRange = fare.Amount >= min && fare.Amount <= max;
            isFareVisible = fareInRange;
            fare.visible = fare.visible ? isFareVisible : fare.visible;
            if (!isFareVisible) {
              fare.filteredBy.push(this.id);
            }
            if (fareInRange) {
              if (!cheapestFare || fare.Amount < cheapestFare.Amount) cheapestFare = fare;
            }
          });
          if (cheapestFare) fc.DisplayFare = cheapestFare;
          fc.visible = !!cheapestFare;
        });
    });
  }

  function filterFlightTime(packages, bumpFiltersApplied) {
    packages[0].Segments.forEach((segment, i) => {
      const option = this.options[i];
      const ceil = this.rzslider.ceil;
      const { defaultMinDepart, defaultMaxDepart, minArrive, maxArrive, minDepart, maxDepart, defaultMinArrive, defaultMaxArrive } = option;
      const isActive = minArrive > defaultMinArrive || maxArrive < ceil || minDepart > defaultMinDepart || maxDepart < ceil;
      if (!isActive) return;
      if (isActive && this.isResultsFilter) bumpFiltersApplied(this, i);
      packages.forEach((pkg) => {
        let segmentVisible = false;
        const segment = pkg.Segments[i];

        const flight = segment.flight;
        const departTime = dateTimeService.getMinutesFromTime(flight.DepartureTime);
        const arriveTime = dateTimeService.getMinutesFromTime(flight.ArrivalTime);
        segmentVisible = departTime >= minDepart && departTime <= maxDepart && arriveTime >= minArrive && arriveTime <= maxArrive;
        segment.visible = segment.visible ? segmentVisible : segment.visible;
        if (!segmentVisible) {
          segment.filteredBy.push(travelService.GetIdSeg(this.id, i));
        }
      });
    });
  }

  function filterCheapestFares() {
    const packages = travelService.packageResults;
    packages.forEach((pkg) => {
      pkg.visible = false;
      pkg.Segments.forEach((segment) => {
        const { Carrier } = segment.flight;
        Object.entries(pkg.FareClasses)
          .filter(([fcName, fc]) => fc)
          .forEach(([fcName, fc]) => {
            fc.visible = false;
            fc.DisplayFare.visible = false;
            fc.Fares.forEach((fare) => (fare.visible = false));
          });
      });
    });

    packages.forEach((pkg) => {
      pkg.Segments.forEach((segment) => {
        const { Carrier } = segment.flight;
        Object.entries(pkg.FareClasses)
          .filter(([fcName, fc]) => fc)
          .forEach(([fcName, fc]) => {
            fc.Fares.forEach((fare) => {
              if (fare.Cheapest) {
                pkg.visible = true;
                fare.visible = true;
                fc.visible = true;
                fc.DisplayFare = fare;
              }
            });
          });
      });
    });
  }

  function filterPackageSegments(packages) {
    const values = packages.flatMap((pkg) =>
      pkg.Segments.map((segment, index) => {
        return { Identifier: segment.flight.Identifier, index, selected: segment.selected };
      }).filter((s) => s.selected)
    );

    if (!values.length) return;
    let filteredItems = [];
    packages[0].Segments.forEach((segment, i) => {
      filteredItems.push(values.find((s) => s.index == i));
    });
    packages.forEach((pkg) => {
      filteredItems.forEach((segment) => {
        if (!segment) return;
        const seg = pkg.Segments[segment.index];
        let isSegmentSelected = seg.flight.Identifier === segment.Identifier;
        seg.selected = isSegmentSelected;
        seg.visible = seg.visible ? isSegmentSelected : seg.visible;
        if (!isSegmentSelected) {
          seg.filteredBy.push(this.id);
        }
      });
    });
  }

  function filterHasIncludeExcludeSelectionsUnset(filter) {
    return !!filter.includeExcludeSelections && filter.includeExcludeSelections.length === 0;
  }

  function onlyUnique(value, index, array) {
    return array.indexOf(value) === index;
  }

  function cleanSelectedList(filters, filter) {
    var selectedList = filters[filter].selected;
    for (var i = 0; i < filters[filter].options.length; i++) {
      if (selectedList[i]) {
        selectedList[i] = selectedList[i].filter(onlyUnique);
      } else {
        selectedList[i] = [];
      }
    }
  }

  function reselectOptions(filters) {
    var filtersToCheck = ['stops', 'carrier', 'cabin', 'policy', 'aircraft', 'flexibility', 'safScore'];
    var allTabIndex = travelService.results.length;

    filtersToCheck.forEach((filter) => {
      cleanSelectedList(filters, filter);
      var selectedList = filters[filter].selected;
      var options = filters[filter].options;
      if (options[options.length - 1]) {
        // Checking all segment options (except the last one since that one is for the ALL tab).
        for (var i = 0; i < options.length - 1; i++) {
          // Checking if the current segment is in the cart.
          if (travelService.results[i] && travelService.results[i].selected) {
            options[i].forEach((option) => {
              // Making sure that the segment's options match the isActive of the ALL tab options.
              var matchingAllTabOption = options[allTabIndex].find((opt) => opt.label === option.label);
              if (matchingAllTabOption && matchingAllTabOption.isActive) {
                option.isActive = matchingAllTabOption.isActive;
                var selected = selectedList[i];

                var hasValue = selected.find((opt) => opt.value === option.value);

                if (option.isActive) {
                  if (!hasValue) {
                    selected.push(option.value);
                  }
                } else {
                  selected.splice(selected.indexOf(option.value), 1);
                }
              }
            });
          }
        }
      }
    });
  }

  function cleanOptions(filters) {
    var filtersToCheck = ['stops', 'carrier', 'cabin', 'policy', 'aircraft', 'flexibility', 'safScore'];

    filtersToCheck.forEach((filter) => {
      cleanSelectedList(filters, filter);

      var options = filters[filter].options;
      if (options[options.length - 1]) {
        // Checking all segment options (except the last one since that one is for the ALL tab).
        var optionsToAddToAllTab = {};
        for (var i = 0; i < options.length - 1; i++) {
          options[i].forEach((option) => {
            if (!option.originalCount) {
              option.originalCount = option.count;
            }

            if (option.originalCount >= 0) {
              if (optionsToAddToAllTab[option.label] && option.originalCount > 0) {
                optionsToAddToAllTab[option.label]++;
              } else {
                optionsToAddToAllTab[option.label] = 1;
              }
            }
          });
        }

        var optionsToRemove = [];
        for (const [label, count] of Object.entries(optionsToAddToAllTab)) {
          if (count != options.length - 1) {
            optionsToRemove.push(label);
          }
        }

        var newFilterList = [];
        options[options.length - 1].forEach((option) => {
          var hasOption = optionsToRemove.find((opt) => opt == option.label);
          if (!hasOption) {
            newFilterList.push(option);
          }
        });

        options[options.length - 1] = newFilterList;
      }
    });
  }

  filterSegmentOptions = flightFilterHelperService.memoize(filterSegmentOptions);
  filterFareOptions = flightFilterHelperService.memoize(filterFareOptions);

  return service;
};

flightFilterPackageService.$inject = $inject;

export default flightFilterPackageService;



// WEBPACK FOOTER //
// ./src/services/flightFilterPackageService.js